import React, { useCallback } from "react";
import { ArticleCard } from "@americanexpress/consumer-travel-base";
import { AdData, ArticleData, ArticleOrAdData, ExploreMoreArticleData} from "../../types/PersonalizedMarketingArticles";
import { useReactlytics } from '@americanexpress/reactlytics';
import { ADS_MODULE_NAME, MODULE_NAME } from "../../constants/moduleConstants";
import { RenderModule } from "holocron";
import { isAd } from "../../utils/articles";
import { ExploreMore } from "../ExploreMore/ExploreMore";
import { useMediaQuery } from "@americanexpress/dls-react";

type articleOrAdProps = {
  data: ArticleOrAdData,
  analyticsElementPrefix?: string,
  index: number,
  adScreenSize: string,
};

const renderAd = (ad: AdData, element: string, track: any, adscreensize: string) => (
  useMediaQuery(adscreensize) ? (
  <RenderModule
    moduleName={ADS_MODULE_NAME}
    props={{
      placementId: ad?.placementId,
      channelName: ad?.channelName,
      placementName: ad?.placementName,
      onAdEvent: ({ type }: { type: string }) => {
        track({
          event: {
            type,
            element,
            identifier: MODULE_NAME,
          },
        });
      },
    }}
  />
) : null);

const renderArticle = (article: ArticleData | ExploreMoreArticleData, elementIdPrefix: string, track: any) => {
  const elementIdSuffix = article?.analyticElement ? `-${article.analyticElement.toLowerCase().replace(/\s+/g, '')}` : '';
  const elementIdentifier = `${elementIdPrefix}${elementIdSuffix}`;
  const triggerOnClickAnalytics = useCallback(() => {
    track({
      event: {
        type: "click",
        element: elementIdentifier,
        identifier: MODULE_NAME,
      },
    })
  }, [elementIdentifier, track]);

  if (article.id === "exploreMore" && 'icon' in article && 'body' in article && 'ctaDestination' in article && 'ctaLabel' in article) {
    return (
      <ExploreMore
        article={article}
        triggerOnClickAnalytics={triggerOnClickAnalytics}
      />
    );
  }

  /* istanbul ignore next -- this is only here to prevent type errors for the ArticleCard return */
  if ('title' in article && 'body' in article && ('imageUrl' in article || 'fallbackImageUrl' in article) && 'ctaDestination' in article) {
    return (
      <ArticleCard
        title={article.title}
        body={article.body}
        imageUrl={article.imageUrl}
        fallbackImageUrl={article.fallbackImageUrl}
        badge={article.attribution}
        triggerOnClickAnalytics={triggerOnClickAnalytics}
        analytics={{
          analyticsElement: elementIdentifier,
          isTrackImpressions: true
        }}
        cta={{
          label: article?.ctaLabel,
          link: article?.ctaDestination,
          showCtaButton: article?.showCtaButton,
        }}
      />
    )
  }

  /* istanbul ignore next -- this is only here to prevent type errors for the ArticleCard return */
  return null;
}

export const ArticleOrAd = ({
  data,
  analyticsElementPrefix,
  index,
  adScreenSize,
}: articleOrAdProps) => {
  const { track } = useReactlytics();
  const elementIdPrefix = analyticsElementPrefix ? `${analyticsElementPrefix}-` : '';

  return isAd(data)
    ? renderAd(data as AdData, `${elementIdPrefix}ad-tile`, track, adScreenSize)
    : renderArticle(data as (ArticleData | ExploreMoreArticleData), `${elementIdPrefix}tile${index + 1}`, track);
};
